import { create } from 'zustand';
import type { Site, Page, Block, BlockType } from '@mawkaa/types';
import { api } from '@/lib/api';
import { createBlock, duplicateBlock, moveBlock } from '@mawkaa/blocks';

interface BuilderState {
  // Current site and page
  site: Site | null;
  currentPage: Page | null;
  pages: Page[];
  blocks: Block[];

  // UI state
  selectedBlockId: string | null;
  hoveredBlockId: string | null;
  isPreviewMode: boolean;
  isSaving: boolean;
  hasUnsavedChanges: boolean;
  devicePreview: 'desktop' | 'tablet' | 'mobile';

  // Actions
  loadSite: (siteId: string) => Promise<void>;
  loadPage: (pageId: string) => Promise<void>;
  savePage: () => Promise<void>;

  // Block actions
  addBlock: (type: BlockType, position?: number) => void;
  updateBlock: (blockId: string, updates: Partial<Block>) => void;
  removeBlock: (blockId: string) => void;
  duplicateBlock: (blockId: string) => void;
  moveBlock: (fromIndex: number, toIndex: number) => void;
  selectBlock: (blockId: string | null) => void;
  hoverBlock: (blockId: string | null) => void;

  // Site actions
  updateSiteSettings: (settings: Partial<Site['settings']>) => void;

  // UI actions
  setPreviewMode: (enabled: boolean) => void;
  setDevicePreview: (device: 'desktop' | 'tablet' | 'mobile') => void;

  // Publishing
  publishSite: () => Promise<string>;
  unpublishSite: () => Promise<void>;
}

export const useBuilderStore = create<BuilderState>((set, get) => ({
  site: null,
  currentPage: null,
  pages: [],
  blocks: [],
  selectedBlockId: null,
  hoveredBlockId: null,
  isPreviewMode: false,
  isSaving: false,
  hasUnsavedChanges: false,
  devicePreview: 'desktop',

  loadSite: async (siteId: string) => {
    const [siteResponse, pagesResponse] = await Promise.all([
      api.get<{ site: Site }>(`/sites/${siteId}`),
      api.get<{ pages: Page[] }>(`/sites/${siteId}/pages`),
    ]);

    set({
      site: siteResponse.site,
      pages: pagesResponse.pages,
    });

    // Load the homepage by default
    const homepage = pagesResponse.pages.find((p) => p.isHomepage) || pagesResponse.pages[0];
    if (homepage) {
      await get().loadPage(homepage.id);
    }
  },

  loadPage: async (pageId: string) => {
    const { site } = get();
    if (!site) return;

    const response = await api.get<{ page: Page & { blocks: Block[] } }>(
      `/sites/${site.id}/pages/${pageId}`
    );

    set({
      currentPage: response.page,
      blocks: response.page.blocks || [],
      selectedBlockId: null,
      hasUnsavedChanges: false,
    });
  },

  savePage: async () => {
    const { site, currentPage, blocks } = get();
    if (!site || !currentPage) return;

    set({ isSaving: true });
    try {
      // Save blocks order and content
      const blockIds = blocks.map((b) => b.id);
      await api.post(`/sites/${site.id}/pages/${currentPage.id}/blocks/reorder`, { blockIds });

      // Save each block that was modified
      for (const block of blocks) {
        await api.patch(`/sites/${site.id}/pages/${currentPage.id}/blocks/${block.id}`, {
          content: block.content,
          settings: block.settings,
        });
      }

      set({ hasUnsavedChanges: false });
    } finally {
      set({ isSaving: false });
    }
  },

  addBlock: (type: BlockType, position?: number) => {
    const { blocks } = get();
    const newBlock = createBlock(type, position ?? blocks.length);

    // Insert at position or end
    const newBlocks = [...blocks];
    if (position !== undefined) {
      newBlocks.splice(position, 0, newBlock);
      // Update order for all blocks
      newBlocks.forEach((b, i) => (b.order = i));
    } else {
      newBlocks.push(newBlock);
    }

    set({
      blocks: newBlocks,
      selectedBlockId: newBlock.id,
      hasUnsavedChanges: true,
    });

    // Save to server
    const { site, currentPage } = get();
    if (site && currentPage) {
      api.post(`/sites/${site.id}/pages/${currentPage.id}/blocks`, {
        type: newBlock.type,
        content: newBlock.content,
        settings: newBlock.settings,
        order: newBlock.order,
      }).then((response: any) => {
        // Update the block with the server ID
        set((state) => ({
          blocks: state.blocks.map((b) =>
            b.id === newBlock.id ? { ...b, id: response.block.id } : b
          ),
          selectedBlockId: response.block.id,
        }));
      });
    }
  },

  updateBlock: (blockId: string, updates: Partial<Block>) => {
    set((state) => ({
      blocks: state.blocks.map((block) =>
        block.id === blockId ? { ...block, ...updates } : block
      ),
      hasUnsavedChanges: true,
    }));
  },

  removeBlock: (blockId: string) => {
    const { site, currentPage, blocks } = get();

    set({
      blocks: blocks.filter((b) => b.id !== blockId),
      selectedBlockId: null,
      hasUnsavedChanges: true,
    });

    if (site && currentPage) {
      api.delete(`/sites/${site.id}/pages/${currentPage.id}/blocks/${blockId}`);
    }
  },

  duplicateBlock: (blockId: string) => {
    const { blocks } = get();
    const blockToDuplicate = blocks.find((b) => b.id === blockId);
    if (!blockToDuplicate) return;

    const newBlock = duplicateBlock(blockToDuplicate);
    const index = blocks.findIndex((b) => b.id === blockId);
    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    newBlocks.forEach((b, i) => (b.order = i));

    set({
      blocks: newBlocks,
      selectedBlockId: newBlock.id,
      hasUnsavedChanges: true,
    });
  },

  moveBlock: (fromIndex: number, toIndex: number) => {
    const { blocks } = get();
    const newBlocks = moveBlock(blocks, fromIndex, toIndex);
    set({ blocks: newBlocks, hasUnsavedChanges: true });
  },

  selectBlock: (blockId: string | null) => {
    set({ selectedBlockId: blockId });
  },

  hoverBlock: (blockId: string | null) => {
    set({ hoveredBlockId: blockId });
  },

  updateSiteSettings: (settings: Partial<Site['settings']>) => {
    const { site } = get();
    if (!site) return;

    const updatedSettings = { ...site.settings, ...settings };
    set({
      site: { ...site, settings: updatedSettings },
      hasUnsavedChanges: true,
    });

    api.patch(`/sites/${site.id}`, { settings });
  },

  setPreviewMode: (enabled: boolean) => {
    set({ isPreviewMode: enabled, selectedBlockId: null });
  },

  setDevicePreview: (device: 'desktop' | 'tablet' | 'mobile') => {
    set({ devicePreview: device });
  },

  publishSite: async () => {
    const { site } = get();
    if (!site) throw new Error('No site loaded');

    const response = await api.post<{ site: Site; url: string }>(`/sites/${site.id}/publish`);
    set({ site: response.site });
    return response.url;
  },

  unpublishSite: async () => {
    const { site } = get();
    if (!site) return;

    await api.post(`/sites/${site.id}/unpublish`);
    set({ site: { ...site, status: 'draft' } });
  },
}));
